import axios, { type AxiosResponse } from 'axios';

/**
 * 백엔드 표준 응답 봉투(envelope).
 * 모든 site-management API 가 `{ success, message, data }` 형태로 응답한다.
 * 도메인별로 `XxxListResponse` / `XxxResponse` / `XxxDeleteResponse` 를
 * 손으로 재선언하던 50여 개 인터페이스를 이 제네릭으로 대체한다.
 */
export interface ApiListResponse<T> {
  success: boolean;
  message: string;
  data: T[];
}

export interface ApiItemResponse<T> {
  success: boolean;
  message: string;
  data?: T;
}

export interface ApiOkResponse {
  success: boolean;
  message: string;
}

/**
 * 단건 응답에서 data 를 꺼낸다.
 * success && data 면 data, 아니면 message(없으면 fallback)로 throw.
 */
export function unwrapItem<T>(resp: AxiosResponse<ApiItemResponse<T>>, errorMessage: string): T {
  if (resp.data.success && resp.data.data) {
    return resp.data.data;
  }
  throw new Error(resp.data.message || errorMessage);
}

/**
 * 목록 응답에서 data 배열을 꺼낸다.
 * success && data 면 data, 아니면 message(없으면 fallback)로 throw.
 */
export function unwrapList<T>(resp: AxiosResponse<ApiListResponse<T>>, errorMessage: string): T[] {
  if (resp.data.success && resp.data.data) {
    return resp.data.data;
  }
  throw new Error(resp.data.message || errorMessage);
}

/**
 * 본문이 없는 응답(삭제 등)의 성공 여부만 검증한다.
 * 실패면 message(없으면 fallback)로 throw.
 */
export function ensureOk(resp: AxiosResponse<ApiOkResponse>, errorMessage: string): void {
  if (!resp.data.success) {
    throw new Error(resp.data.message || errorMessage);
  }
}

/**
 * 요청이 4xx·5xx 로 실패하면 백엔드가 담은 사유(message)를 그대로 올린다 — 통합서버·현장 장비를
 * 거치는 요청은 사유가 화면에 그대로 보여야 조치할 수 있다.
 */
export async function withServerMessage<T>(req: () => Promise<T>): Promise<T> {
  try {
    return await req();
  } catch (e) {
    const message = axios.isAxiosError(e) ? e.response?.data?.message : undefined;
    throw typeof message === 'string' && message ? new Error(message) : e;
  }
}
